import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { findHoliday, getSGHolidays, holidayName } from "@/lib/holidays-sg";

const COPY = {
  de: {
    title: "Geplante Schließtage",
    sub: "Feiertage Kanton St. Gallen",
    today_label: "Heute",
    short_weekdays: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
    locale: "de-CH",
  },
  fr: {
    title: "Jours de fermeture",
    sub: "Jours fériés · Canton de St-Gall",
    today_label: "Aujourd'hui",
    short_weekdays: ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."],
    locale: "fr-CH",
  },
  it: {
    title: "Giorni di chiusura",
    sub: "Festività · Cantone San Gallo",
    today_label: "Oggi",
    short_weekdays: ["dom", "lun", "mar", "mer", "gio", "ven", "sab"],
    locale: "it-CH",
  },
  en: {
    title: "Upcoming closure days",
    sub: "Public holidays · Canton of St. Gallen",
    today_label: "Today",
    short_weekdays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    locale: "en-GB",
  },
};

const MAX_ITEMS = 6;

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// Returns the holidays of the current year that are still ahead (including
// today) followed by next year's full list, capped to MAX_ITEMS + maybe
// today. Today's holiday, if any, is delivered separately so the UI can
// emphasise it.
function buildList(today) {
  const thisYear = getSGHolidays(today.getFullYear());
  const nextYear = getSGHolidays(today.getFullYear() + 1);
  const all = [...thisYear, ...nextYear].filter(
    (h) => h.date >= new Date(today.getFullYear(), today.getMonth(), today.getDate())
  );
  const todayHoliday = all.find((h) => isSameDay(h.date, today)) || null;
  const upcoming = all.filter((h) => !isSameDay(h.date, today)).slice(0, MAX_ITEMS);
  return { todayHoliday, upcoming };
}

export default function UpcomingHolidays({ className = "" }) {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // Refresh roughly hourly so the list rolls over correctly at midnight
    // even if the visitor leaves the tab open.
    const t = setInterval(() => setNow(new Date()), 60 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Direct lookup via findHoliday so we behave consistently with OfficeStatus
  // — buildList is used purely to derive the future list.
  const todayHoliday = findHoliday(now);
  const { upcoming } = buildList(now);

  const fmtDate = (d) =>
    d.toLocaleDateString(c.locale, { day: "2-digit", month: "long" });

  return (
    <div
      data-testid="upcoming-holidays"
      className={`border border-white/10 bg-[#101013] ${className}`}
    >
      <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
        <CalendarDays className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] text-white">
            {c.title}
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{c.sub}</div>
        </div>
      </div>

      <ol className="divide-y divide-white/5">
        {todayHoliday && (
          <li
            data-testid="upcoming-holidays-today"
            className="flex items-baseline gap-4 px-5 py-3 bg-amber-400/[0.04]"
          >
            <span className="inline-flex items-center justify-center min-w-[64px] px-2 py-0.5 border border-amber-400/40 text-amber-300 text-[10px] tracking-[0.16em] uppercase">
              {c.today_label}
            </span>
            <span className="font-display text-white text-base tracking-tight flex-1 truncate">
              {holidayName(todayHoliday.key, lang)}
            </span>
            <span className="text-[11px] text-zinc-500 tabular-nums whitespace-nowrap">
              {c.short_weekdays[todayHoliday.date.getDay()]}
              {" · "}
              {fmtDate(todayHoliday.date)}
            </span>
          </li>
        )}

        {upcoming.map((h, idx) => (
          <li
            key={h.date.toISOString()}
            data-testid={`upcoming-holidays-item-${idx}`}
            className="flex items-baseline gap-4 px-5 py-3 hover:bg-white/[0.02] transition-colors"
          >
            <span className="text-[11px] text-zinc-500 tabular-nums whitespace-nowrap min-w-[64px]">
              {c.short_weekdays[h.date.getDay()]}
              {" · "}
              {fmtDate(h.date)}
            </span>
            <span className="text-zinc-300 text-sm flex-1 truncate">
              {holidayName(h.key, lang)}
            </span>
            <span className="text-[10px] text-zinc-600 tabular-nums">
              {h.date.getFullYear()}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
